import { Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faTimes, faDivide, faPercent } from '@fortawesome/free-solid-svg-icons';

const { Text } = Typography;

const OPERATION_LABELS = {
  add: <FontAwesomeIcon icon={faPlus} />,
  sub: <FontAwesomeIcon icon={faMinus} />,
  mul: <FontAwesomeIcon icon={faTimes} />,
  div: <FontAwesomeIcon icon={faDivide} />,
  pct: <FontAwesomeIcon icon={faPercent} />,
  max: <Text strong style={{ color: 'inherit' }}>MAX</Text>,
  min: <Text strong style={{ color: 'inherit' }}>MIN</Text>,
};

const FUNCTION_STYLE_OPERATIONS = ['min', 'max', 'pct'];

const VALUE_LABELS = {
  insured_value: 'Valor asegurado',
  rate: 'Tasa',
  vat: 'IVA',
  fixed: 'Valor fijo',
};

/** Colores para los paréntesis (por profundidad) */
const PAREN_COLORS = [
  'rgba(24, 144, 255, 0.85)',
  'rgba(82, 196, 26, 0.8)',
  'rgba(250, 173, 20, 0.85)',
  'rgba(114, 46, 209, 0.75)',
  'rgba(0, 185, 185, 0.8)',
];

function getParenColor(depth) {
  return PAREN_COLORS[depth % PAREN_COLORS.length];
}

function valueLabel(node) {
  if (node.valueType === 'fixed') {
    return `${node.valueName ?? 'Valor fijo'}: ${node.fixedValue ?? ''}`;
  }
  return VALUE_LABELS[node.valueType] ?? node.valueName ?? node.valueType ?? 'Valor';
}

/**
 * Renderiza el árbol de expresión en una línea; solo los paréntesis ( ) tienen color.
 */
export function ExpressionSummaryDisplay({ node, depth = 0 }) {
  if (!node) return null;
  if (node.nodeType === 'value') {
    return valueLabel(node);
  }
  if (node.nodeType !== 'operation' || !node.children?.length) {
    return null;
  }
  const opType = node.operationType;
  const op = OPERATION_LABELS[opType] ?? opType;
  const sortedChildren = node.children
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const isFunctionStyle = FUNCTION_STYLE_OPERATIONS.includes(opType);
  const parenColor = getParenColor(depth);
  const childParenColor = getParenColor(depth + 1);

  const Paren = ({ open }) => (
    <span style={{ color: parenColor, fontWeight: 700, fontSize: '1.2em' }}>{open ? '(' : ')'}</span>
  );
  const ChildParen = ({ open }) => (
    <span style={{ color: childParenColor, fontWeight: 700, fontSize: '1.2em' }}>{open ? '(' : ')'}</span>
  );

  if (isFunctionStyle) {
    const inner = sortedChildren.map((child, i) => (
      <span key={i}>
        {i > 0 && ', '}
        {child.nodeType === 'operation' ? (
          <>
            <ChildParen open />
            <ExpressionSummaryDisplay node={child} depth={depth + 1} />
            <ChildParen open={false} />
          </>
        ) : (
          valueLabel(child)
        )}
      </span>
    ));
    return (
      <span>
        <span style={{ color: parenColor }}>{op}</span>
        <Paren open />
        {inner}
        <Paren open={false} />
      </span>
    );
  }

  const parts = sortedChildren.map((child, i) => (
    <span key={i}>
      {i > 0 && <span style={{ color: parenColor }}> {op} </span>}
      {child.nodeType === 'operation' ? (
        <>
          <ChildParen open />
          <ExpressionSummaryDisplay node={child} depth={depth + 1} />
          <ChildParen open={false} />
        </>
      ) : (
        valueLabel(child)
      )}
    </span>
  ));
  return <span>{parts}</span>;
}
